import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { getEventsGroupedByYear } from '../services/googleSheets';
import { getPrimarySpeaker } from '../utils/getPrimarySpeaker';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';
import FloatingShareButton from '../components/FloatingShareButton';
import { metaTagsService } from '../services/metaTagsService';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * EventsPage component - displays events archive grouped by year
 * @returns {JSX.Element} EventsPage component
 */
const EventsPage = () => {
  const { eventName } = useParams();
  const navigate = useNavigate();
  const { config, theme, loading: configLoading, error: configError } = useConfig();
  const [eventsByYear, setEventsByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobileView = useMediaQuery('(max-width: 768px)');

  // Define colors based on theme (auto-adapts to dark/light backgrounds)
  const colors = {
    primary: theme?.colors?.primary || config?.colors?.primary || '#1e4273',
    secondary: theme?.colors?.secondary || config?.colors?.secondary || '#f5f7fa',
    text: theme?.colors?.text || '#333333',
    textSecondary: theme?.colors?.darkGray || '#666666',
    cardText: theme?.colors?.cardText || '#333333',
    cardTextSecondary: theme?.colors?.cardTextSecondary || '#666666',
    lightGray: theme?.colors?.lightGray || '#f0f2f5',
    background: theme?.colors?.background || config?.colors?.background || '#f8f9fa',
    headingColor: theme?.colors?.headingColor || config?.colors?.primary || '#1e4273',
    linkColor: theme?.colors?.linkColor || config?.colors?.primary || '#1e4273'
  };

  
  // Fetch events data when component mounts or config changes
  useEffect(() => {
    const loadEventsData = async () => {
      // Wait for config
      if (configLoading || !config) {
        console.log('EventsPage: Waiting for config...');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('EventsPage: Loading events archive...');
        
        // Use the Google Sheet ID from config
        const sheetId = config.google_sheet_id;
        if (!sheetId) {
          throw new Error('No Google Sheet ID found in configuration');
        }

        const eventsData = await getEventsGroupedByYear(sheetId);
        setEventsByYear(eventsData);
        
        console.log('EventsPage: Loaded events by year:', Object.keys(eventsData));
      } catch (error) {
        console.error('EventsPage: Error loading events:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadEventsData();
  }, [config, configLoading]);

  // Update meta tags when component mounts or config changes
  useEffect(() => {
    if (config && !configLoading) {
      const logoUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
      
      metaTagsService.updateMetaTags({
        title: `Events & Series - ${config.name || 'Penta University'}`,
        description: 'Join our upcoming events and lecture series',
        image: logoUrl,
        url: `${window.location.origin}/events`,
        type: 'website'
      });
    }

    // Cleanup function to reset meta tags when component unmounts
    return () => {
      if (config) {
        metaTagsService.resetToDefaults();
      }
    };
  }, [config, configLoading]);

  // Show loading state
  if (configLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading events archive...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (configError || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Unable to Load Events
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {configError || error || 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Get total events count
  const totalEvents = Object.values(eventsByYear).reduce((sum, events) => sum + events.length, 0);
  const years = Object.keys(eventsByYear).map(Number).sort((a, b) => b - a); // Sort years descending

  // Main content
  const content = (
    <div style={{ 
      maxWidth: '1400px', 
      margin: '0 auto', 
      padding: isMobileView ? '20px' : '40px' 
    }}>
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '40px' 
      }}>
        <h1 style={{
          fontSize: isMobileView ? '1.75rem' : '2.5rem',
          fontWeight: 'bold',
          color: colors.headingColor,
          marginBottom: '16px',
          fontFamily: config?.fonts?.heading || '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          letterSpacing: '-0.5px'
        }}>
          Events Archive
        </h1>
        <p style={{ 
          color: colors.text,
          fontSize: isMobileView ? '1rem' : '1.2rem',
          fontWeight: '400',
          fontFamily: config?.fonts?.body || '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          maxWidth: '800px',
          margin: '0 auto',
          lineHeight: '1.6',
          padding: isMobileView ? '0 15px' : '0'
        }}>
          Browse {totalEvents} events across {years.length} {years.length === 1 ? 'year' : 'years'}
        </p>
      </div>

      {/* Year Sections */}
      {years.length > 0 ? (
        isMobileView ? (
          // Mobile Layout - Year boxes with horizontal scroll
          years.map(year => (
            <div key={year} style={{ 
              marginBottom: '30px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {/* Year Header */}
              <div style={{ 
                backgroundColor: colors.primary,
                color: 'white',
                padding: '15px 20px',
                fontSize: '20px',
                fontWeight: 'bold',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{year}</span>
                <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                  {eventsByYear[year].length} {eventsByYear[year].length === 1 ? 'event' : 'events'}
                </span>
              </div>
              
              {/* Horizontal scroll container */}
              <div style={{ 
                display: 'flex',
                overflowX: 'auto',
                padding: '20px',
                gap: '15px',
                WebkitOverflowScrolling: 'touch'
              }}>
                {eventsByYear[year].map((event, index) => (
                  <Link 
                    key={`${event.id}-${index}`}
                    to={`/series/${encodeURIComponent(event.name)}`}
                    style={{ textDecoration: 'none', flexShrink: 0 }}
                  >
                    <div style={{
                      width: '280px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: `1px solid ${colors.lightGray}`,
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease',
                    }}
                    onClick={(e) => {
                      const element = e.currentTarget;
                      if (element && element.style) {
                        element.style.transform = 'scale(0.98)';
                        setTimeout(() => {
                          if (element && element.style) {
                            element.style.transform = 'scale(1)';
                          }
                        }, 100);
                      }
                    }}
                    >
                      {/* Mobile Event Card Header */}
                      <div style={{
                        backgroundColor: colors.secondary,
                        padding: '12px',
                        borderBottom: `1px solid ${colors.lightGray}`,
                        minHeight: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <h3 style={{ 
                          fontSize: '16px',
                          fontWeight: 'bold',
                          margin: 0,
                          color: colors.cardText,
                          lineHeight: '1.3',
                          textAlign: 'center'
                        }}>
                          {event.name}
                        </h3>
                      </div>
                      
                      {/* Mobile Event Card Content */}
                      <div style={{ padding: '12px' }}>
                        {/* Month/Year */}
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          padding: '8px',
                          marginBottom: '10px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: colors.primary,
                          border: `1px solid ${colors.lightGray}`
                        }}>
                          {event.month ? `${event.month} ${event.year}` : event.year}
                        </div>
                        
                        <div style={{
                          fontSize: '14px',
                          color: colors.cardTextSecondary,
                          marginBottom: '8px'
                        }}>
                          {event.primarySpeaker || 'Multiple speakers'}
                        </div>
                        
                        <div style={{ 
                          fontSize: '13px',
                          color: colors.primary,
                          fontWeight: '600'
                        }}>
                          {event.lectureCount} {event.lectureCount === 1 ? 'lecture' : 'lectures'} →
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))
        ) : (
          // Desktop Layout - Grid
          years.map(year => (
            <div key={year} style={{ marginBottom: '60px' }}>
              {/* Year Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '30px',
                borderBottom: `2px solid ${colors.linkColor}`,
                paddingBottom: '15px'
              }}>
                <h2 style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: colors.headingColor,
                  margin: 0,
                  marginRight: '20px'
                }}>
                  {year}
                </h2>
                <div style={{ 
                  fontSize: '16px',
                  color: colors.textSecondary,
                  fontWeight: '500'
                }}>
                  {eventsByYear[year].length} {eventsByYear[year].length === 1 ? 'event' : 'events'}
                </div>
              </div>
              
              {/* Events Grid - Desktop */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '30px'
              }}>
                {eventsByYear[year].map((event, index) => (
                  <Link 
                    key={`${event.id}-${index}`}
                    to={`/series/${encodeURIComponent(event.name)}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      border: `1px solid ${colors.lightGray}`,
                    }}
                    onMouseEnter={(e) => {
                      const element = e.currentTarget;
                      if (element && element.style) {
                        element.style.transform = 'translateY(-5px)';
                        element.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const element = e.currentTarget;
                      if (element && element.style) {
                        element.style.transform = 'translateY(0)';
                        element.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      }
                    }}
                    >
                      {/* Event Header */}
                      <div style={{
                        backgroundColor: colors.primary,
                        padding: '20px',
                        color: 'white',
                        textAlign: 'center',
                        minHeight: '80px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <h3 style={{ 
                          fontSize: '20px',
                          fontWeight: 'bold',
                          margin: 0,
                          lineHeight: '1.3'
                        }}>
                          {event.name}
                        </h3>
                      </div>
                      
                      {/* Event Content */}
                      <div style={{ 
                        padding: '25px',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          {/* Month/Year in grey box */}
                          <div style={{
                            backgroundColor: colors.lightGray,
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '15px',
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: colors.primary
                          }}>
                            {event.month ? `${event.month} ${event.year}` : event.year}
                          </div>
                          
                          <div style={{
                            fontSize: '16px',
                            color: colors.cardTextSecondary,
                            marginBottom: '15px',
                            fontWeight: '500'
                          }}>
                            <span style={{ color: colors.primary }}>Speaker:</span> {event.primarySpeaker || 'Multiple speakers'}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: colors.cardTextSecondary,
                            marginBottom: '20px'
                          }}>
                            {event.lectureCount} {event.lectureCount === 1 ? 'lecture' : 'lectures'}
                          </div>
                        </div>
                        
                        {/* Action Button */}
                        <div style={{
                          backgroundColor: colors.primary,
                          color: 'white',
                          padding: '12px 20px',
                          borderRadius: '6px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'background-color 0.3s ease'
                        }}>
                          View Event →
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))
        )
      ) : (
        // Empty State - matching homepage style
        <div style={{ 
          textAlign: 'center', 
          padding: '80px 20px',
          color: colors.textSecondary
        }}>
          <div style={{ fontSize: '72px', marginBottom: '20px' }}>📅</div>
          <h3 style={{ 
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            marginBottom: '10px'
          }}>
            No Events Found
          </h3>
          <p style={{ fontSize: '16px' }}>
            No events are available in the archive at the moment.
          </p>
        </div>
      )}
    </div>
  );

  // Layout wrapper
  const Layout = isMobileView ? MobileLayout : DesktopLayout;

  return (
    <Layout>
      <div style={{ 
        minHeight: '100vh',
        backgroundColor: colors.background
      }}>
        {content}
      </div>
      <FloatingShareButton 
        pageTitle={`Events Archive - ${config?.name || ''}`}
        pageDescription="Browse our collection of past events and lectures"
      />
    </Layout>
  );
};

export default EventsPage;